import { useState, useEffect } from "react";
import { Play, History, TrendingUp, DollarSign, Activity, BarChart3, Database, AlertTriangle, CheckCircle } from "lucide-react";
import { API_BASE } from "@/lib/api";

interface TradeResult {
  timestamp: number;
  market: string;
  side: string;
  entry_price: number;
  exit_price: number;
  shares: number;
  pnl: number;
  fees: number;
}

interface BacktestResultEntry {
  strategy: string;
  total_trades: number;
  winning_trades: number;
  losing_trades: number;
  win_rate: number;
  total_pnl: number;
  avg_pnl: number;
  max_drawdown: number;
  sharpe_ratio: number;
  total_fees: number;
  total_slippage: number;
  trades: TradeResult[];
}

export default function BacktestPage() {
  const [symbols, setSymbols] = useState("BTC-5MIN,ETH-5MIN");
  const [startDate, setStartDate] = useState("2024-01-01");
  const [endDate, setEndDate] = useState("2026-05-01");
  const [bankroll, setBankroll] = useState(10000);
  const [minGap, setMinGap] = useState(15);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Record<string, BacktestResultEntry> | null>(null);
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  const [datasetInfo, setDatasetInfo] = useState<any>(null);
  const [strategyType, setStrategyType] = useState("latency_arb");

  useEffect(() => {
    fetchDatasetInfo();
  }, []);

  const fetchDatasetInfo = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/backtest/dataset/info`);
      const data = await r.json();
      setDatasetInfo(data);
    } catch (e) { console.error(e); }
  };

  const runBacktest = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/backtest/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          symbols: symbols.split(",").map((s) => s.trim()),
          start_date: startDate,
          end_date: endDate,
          bankroll,
          min_gap: minGap,
        }),
      });
      const data = await r.json();
      setResults(data.results || null);
      if (data.results) {
        setSelectedSymbol(Object.keys(data.results)[0]);
      }
    } catch (e) {
      alert("Backtest failed. Check console.");
    }
    setLoading(false);
  };

  const selectedResult = selectedSymbol && results ? results[selectedSymbol] : null;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <History size={18} className="text-terminal-accent" />
            BACKTEST ENGINE
          </h1>
          <p className="text-xs text-gray-500 mt-1">Vectorized + Event-driven with leakage-proof features and deflated Sharpe</p>
        </div>
        <div className="flex gap-2">
          <span className="px-2 py-1 text-[10px] border border-blue-600 text-blue-500 bg-blue-600/10">
            <Database size={10} className="inline mr-1" />
            Jon-Becker Dataset
          </span>
        </div>
      </div>

      {/* Dataset Info */}
      {datasetInfo && (
        <div className="terminal-window p-3">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Database size={12} />
            <span>Integrated Datasets:</span>
            {datasetInfo.datasets?.map((d: any, i: number) => (
              <span key={i} className="px-2 py-0.5 border border-terminal-accent text-terminal-accent text-[10px]">
                {d.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Config Panel */}
      <div className="terminal-window p-4">
        <div className="grid grid-cols-6 gap-4 items-end">
          <div>
            <label className="text-xs text-gray-500 block mb-1">SYMBOLS</label>
            <input type="text" value={symbols} onChange={(e) => setSymbols(e.target.value)} className="terminal-input w-full text-xs" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">START</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="terminal-input w-full text-xs" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">END</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="terminal-input w-full text-xs" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">BANKROLL ($)</label>
            <input type="number" value={bankroll} onChange={(e) => setBankroll(parseFloat(e.target.value))} className="terminal-input w-full text-xs" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">STRATEGY</label>
            <select value={strategyType} onChange={(e) => setStrategyType(e.target.value)} className="terminal-input w-full text-xs">
              <option value="latency_arb">Latency Arbitrage</option>
              <option value="mean_reversion">Mean Reversion</option>
              <option value="momentum">Momentum</option>
            </select>
          </div>
          <button onClick={runBacktest} disabled={loading} className="terminal-btn-primary flex items-center justify-center gap-2 text-xs py-2">
            {loading ? <Activity size={14} className="animate-spin" /> : <Play size={14} />}
            RUN BACKTEST
          </button>
        </div>
        <div className="mt-3 flex items-center gap-4">
          <label className="text-xs text-gray-500">MIN GAP (cents):</label>
          <input type="range" min={5} max={50} value={minGap} onChange={(e) => setMinGap(parseInt(e.target.value))} className="flex-1 accent-terminal-accent" />
          <span className="text-xs text-terminal-accent w-8">{minGap}c</span>
          {minGap < 10 && <AlertTriangle size={12} className="text-yellow-400" />}
          {minGap >= 15 && <CheckCircle size={12} className="text-green-400" />}
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="space-y-4">
          <div className="grid grid-cols-6 gap-4">
            {Object.entries(results).map(([sym, res]) => (
              <div key={sym} onClick={() => setSelectedSymbol(sym)} className={`terminal-window p-3 cursor-pointer transition-all ${selectedSymbol === sym ? "border-terminal-accent" : ""}`}>
                <div className="text-xs text-gray-500">{sym}</div>
                <div className={`text-lg font-bold ${res.total_pnl >= 0 ? "text-green-400" : "text-red-400"}`}>
                  ${res.total_pnl.toFixed(2)}
                </div>
                <div className="text-[10px] text-gray-500 mt-1">{res.total_trades} trades | {(res.win_rate * 100).toFixed(1)}% WR</div>
              </div>
            ))}
          </div>

          {selectedResult && (
            <div className="grid grid-cols-2 gap-4">
              <div className="terminal-window">
                <div className="terminal-header">
                  <span className="flex items-center gap-2"><BarChart3 size={12} /> PERFORMANCE</span>
                  <span className="text-terminal-accent">{selectedSymbol}</span>
                </div>
                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <StatBox label="TOTAL TRADES" value={selectedResult.total_trades} />
                    <StatBox label="WIN RATE" value={`${(selectedResult.win_rate * 100).toFixed(1)}%`} color={selectedResult.win_rate > 0.5 ? "text-green-400" : "text-red-400"} />
                    <StatBox label="SHARPE" value={selectedResult.sharpe_ratio.toFixed(2)} />
                    <StatBox label="MAX DD" value={`${(selectedResult.max_drawdown * 100).toFixed(1)}%`} color="text-yellow-400" />
                    <StatBox label="TOTAL FEES" value={`$${selectedResult.total_fees.toFixed(2)}`} />
                    <StatBox label="SLIPPAGE" value={`$${selectedResult.total_slippage.toFixed(2)}`} />
                  </div>
                </div>
              </div>

              <div className="terminal-window">
                <div className="terminal-header">
                  <span className="flex items-center gap-2"><TrendingUp size={12} /> TRADE LOG</span>
                  <span className="text-gray-500">{selectedResult.trades.length} entries</span>
                </div>
                <div className="p-0 max-h-64 overflow-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-terminal-border text-gray-500 text-xs">
                        <th className="text-left px-2 py-2">SIDE</th>
                        <th className="text-right px-2 py-2">ENTRY</th>
                        <th className="text-right px-2 py-2">EXIT</th>
                        <th className="text-right px-2 py-2">SHARES</th>
                        <th className="text-right px-2 py-2">PnL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedResult.trades.slice().reverse().map((t, i) => (
                        <tr key={i} className="border-b border-terminal-border/50 hover:bg-white/5">
                          <td className={`px-2 py-1.5 text-xs ${t.side === "YES" ? "text-green-400" : "text-red-400"}`}>{t.side}</td>
                          <td className="px-2 py-1.5 text-right text-xs">{t.entry_price.toFixed(2)}</td>
                          <td className="px-2 py-1.5 text-right text-xs">{t.exit_price.toFixed(2)}</td>
                          <td className="px-2 py-1.5 text-right text-xs">{t.shares.toFixed(0)}</td>
                          <td className={`px-2 py-1.5 text-right text-xs ${t.pnl >= 0 ? "text-green-400" : "text-red-400"}`}>
                            {t.pnl >= 0 ? "+" : ""}${t.pnl.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, color = "text-white" }: { label: string; value: string | number; color?: string }) {
  return (
    <div className="border border-terminal-border rounded p-2">
      <div className="text-[10px] text-gray-500">{label}</div>
      <div className={`text-sm font-bold ${color}`}>{value}</div>
    </div>
  );
}
